import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { DriverPlannings } from "./driverPlanningsApi";
import * as XLSX from "xlsx";
import { authAxios, QueryStatus } from "../../../../utils";
import { RootState } from "../../../../redux/store";
import type { DriverPlanningInputLine } from "./driverPlanningFormat/driverPlanningsHeaders";
import {
  normalizeDriverPlanningColumns,
  parseLine,
  validate,
} from "./driverPlanningFormat/driverPlanningsConnectors";

export type DriverPlanningsState = {
  driverPlannings: DriverPlannings[];
  driverPlanningsStatus: QueryStatus;
  uploadProgress: number;
};

const initialState: DriverPlanningsState = {
  driverPlannings: [],
  driverPlanningsStatus: "idle",
  uploadProgress: 0,
};

export const processFile = (file: File): Promise<DriverPlannings[]> =>
  new Promise((resolve, reject) => {
    const fileReader = new FileReader();
    fileReader.readAsArrayBuffer(file);
    fileReader.onload = (event) => {
      if (event?.target) {
        try {
          const bufferArray = event.target.result;
          const workBook = XLSX.read(bufferArray, { type: "buffer" });
          const sheet = workBook.Sheets[workBook.SheetNames[0]];
          const rawJsonData: Record<string, unknown>[] =
            XLSX.utils.sheet_to_json<Record<string, unknown>>(sheet, {
              raw: false,
            });
          const jsonData: DriverPlanningInputLine[] = rawJsonData.map(
            normalizeDriverPlanningColumns,
          );
          if (!validate(jsonData)) {
            throw new Error("Invalid data format");
          }
          const data = jsonData.map(parseLine);
          resolve(data);
        } catch (e) {
          reject(e);
        }
      }
    };
  });
export const uploadDriverPlanningsFileThunk = createAsyncThunk(
  "driverPlannings/upload",
  async (payload: { file: File; companyUuid: string }, thunkAPI) => {
    const driverPlannings = await processFile(payload.file);
    const company = (thunkAPI.getState() as RootState).home.company;
    const axios = authAxios({ companyUuid: company?.uuid });

    //gestion des gros fichiers de données
    const chunkSize = 1000;
    const totalChunks = Math.ceil(driverPlannings.length / chunkSize);

    for (let i = 0; i < driverPlannings.length; i += chunkSize) {
      const chunk = driverPlannings.slice(i, i + chunkSize);
      await axios.post("driver_plannings", {
        driverPlannings: chunk,
        companyUuid: payload.companyUuid,
      });

      // Mettre à jour la progression
      const currentChunk = Math.floor(i / chunkSize) + 1;
      const progress = Math.round((currentChunk / totalChunks) * 100);
      thunkAPI.dispatch(
        driverPlanningsSlice.actions.setUploadProgress(progress),
      );
    }
  },
);

export const driverPlanningsSlice = createSlice({
  name: "driverPlannings",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    resetPlanningUploadStatus(state) {
      state.driverPlanningsStatus = "idle";
      state.uploadProgress = 0;
    },
    setUploadProgress(state, action: { payload: number }) {
      state.uploadProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(uploadDriverPlanningsFileThunk.pending, (state) => {
        state.driverPlanningsStatus = "processing";
      })
      .addCase(uploadDriverPlanningsFileThunk.fulfilled, (state) => {
        state.driverPlanningsStatus = "success";
      })
      .addCase(uploadDriverPlanningsFileThunk.rejected, (state) => {
        state.driverPlanningsStatus = "failed";
      });
  },
});

export const selectDriverPlanningsStatus = (state: RootState) =>
  state.driverPlannings.driverPlanningsStatus;

export const selectDriverPlanningsUploadProgress = (state: RootState) =>
  state.driverPlannings.uploadProgress;

export default driverPlanningsSlice.reducer;
